import React from "react"

import type { AgentRecord } from "@/types"
import { Alert, AlertTitle } from "@coval/ui"
import { AlertCircle } from "lucide-react"

import { getSimulatorType } from "@/lib/simulatorType"
import { JsonEditorAutoSizeProvider } from "@/components/JsonCodeEditor"
import type { SimulatorType } from "@/types/api/SimulatorType"
import type { FormData } from "@/types/FormData"

import { getRequiredFieldsForSimulator } from "../utils/simulatorConfiguration"
import { validateAgentCreate } from "../utils/validateAgentCreate"
import { AgentPreviewWrapper } from "./AgentPreviewWrapper"
import type { AgentTestConnectionConfig } from "./AgentTestConnectionButton"

const EMPTY_VALIDATION_ISSUES: readonly string[] = []

interface AgentFormProps {
  formData: FormData
  onFormDataChange: (data: FormData) => void
  onSimulatorTypeChange?: (type: SimulatorType) => void
  currentAgent?: AgentRecord
  testConnection?: AgentTestConnectionConfig | null
  showValidationIssues?: boolean
  validationIssues?: readonly string[]
  sectioned?: boolean
}

export const AgentForm: React.FC<AgentFormProps> = ({
  formData,
  onFormDataChange,
  onSimulatorTypeChange,
  currentAgent,
  testConnection,
  showValidationIssues = false,
  validationIssues = EMPTY_VALIDATION_ISSUES,
  sectioned = false,
}) => {
  const simulatorType = getSimulatorType(formData.model_type)
  const connectionComplete =
    validateAgentCreate(
      formData,
      simulatorType ? getRequiredFieldsForSimulator(simulatorType) : [],
      {
        validateName: false,
        persistedModelType: currentAgent?.model_type,
        persistedMediaEncryption: currentAgent?.metadata?.media_encryption,
      },
    ) === null

  const connectionFields = (
    <JsonEditorAutoSizeProvider maxLines={10}>
      <AgentPreviewWrapper
        formData={formData}
        onFormDataChange={onFormDataChange}
        onSimulatorTypeChange={onSimulatorTypeChange}
        testConnection={testConnection}
        sectioned={sectioned}
        connectionComplete={connectionComplete}
        isSavedAgent={Boolean(currentAgent)}
      />
    </JsonEditorAutoSizeProvider>
  )
  const validationAlert =
    showValidationIssues && validationIssues.length > 0 ? (
      <Alert variant="danger" icon={<AlertCircle />}>
        <AlertTitle>Complete the required agent configuration</AlertTitle>
        <ul className="mt-2 list-disc space-y-1 pl-4">
          {validationIssues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      </Alert>
    ) : null

  if (!sectioned) {
    return (
      <div className="w-full space-y-8">
        {validationAlert}
        {connectionFields}
      </div>
    )
  }

  return (
    <div className="w-full space-y-3">
      {validationAlert}
      {connectionFields}
    </div>
  )
}
